import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  useTheme,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyIcon from "@mui/icons-material/Key";
import InfoIcon from "@mui/icons-material/Info";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ExtensionIcon from "@mui/icons-material/Extension";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../config";

const baseUrl = config.baseURL;

// --- Color Constants ---
const BACKGROUND_COLOR = "#D1D8E0";
const PRIMARY_TEXT_COLOR = "#2E4053";
const ACCENT_COLOR = "#34495E";

// --- Webhook Guide Component ---
const WebhookGuide = ({ muiTheme }) => (
  <Box sx={{ mt: 4, mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 600,
        color: PRIMARY_TEXT_COLOR,
        mb: muiTheme.spacing(2),
      }}
    >
      How to Connect Your Google Form to ERP
    </Typography>

    <Typography
      variant="body2"
      sx={{ mb: muiTheme.spacing(2), color: ACCENT_COLOR }}
    >
      Follow these steps to automatically send form submissions (including main
      products, upsells, and gifts) to your ERP system.
    </Typography>

    <List dense>
      {[
        {
          icon: <AssignmentIcon sx={{ color: ACCENT_COLOR }} />,
          primary: "Step 1: Open your Google Form",
          secondary: "Open the form you want to connect.",
        },
        {
          icon: <ExtensionIcon sx={{ color: ACCENT_COLOR }} />,
          primary: "Step 2: Install 'Webhook for Google Forms' Add-on",
          secondary: (
            <>
              Go to Add-ons &rarr; Search "Webhook for Google Forms" &rarr;
              Install
            </>
          ),
        },
        {
          icon: <ExtensionIcon sx={{ color: ACCENT_COLOR }} />,
          primary: "Step 3: Set up webhook",
          secondary: "Open the add-on &rarr; Set up webhook &rarr; Paste URL.",
        },
        {
          icon: <ContentPasteIcon sx={{ color: ACCENT_COLOR }} />,
          primary: "Step 4: Paste your ERP Webhook URL",
          secondary: (
            <>
              Set Method: POST, Format: JSON, Send on form submit: ✅ Enabled.
              Click Save.
            </>
          ),
        },
        {
          icon: <SendIcon sx={{ color: ACCENT_COLOR }} />,
          primary: "Step 5: Test your webhook",
          secondary:
            "Submit the form and confirm the submission appears in ERP logs.",
        },
      ].map((step, idx) => (
        <ListItem key={idx}>
          <ListItemIcon>{step.icon}</ListItemIcon>
          <ListItemText
            primary={step.primary}
            secondary={step.secondary}
            primaryTypographyProps={{
              fontWeight: 500,
              color: PRIMARY_TEXT_COLOR,
            }}
            secondaryTypographyProps={{ color: ACCENT_COLOR }}
          />
        </ListItem>
      ))}
    </List>

    <Typography
      variant="caption"
      display="block"
      sx={{ mt: 3, color: PRIMARY_TEXT_COLOR, fontWeight: 500 }}
    >
      <CheckCircleIcon
        sx={{
          verticalAlign: "middle",
          fontSize: "1rem",
          mr: 0.5,
          color: ACCENT_COLOR,
        }}
      />{" "}
      Tips for success:
    </Typography>
    <List dense sx={{ ml: 2 }}>
      <ListItem disablePadding>
        <ListItemText
          primary="Copy the full Webhook URL from ERP dashboard."
          primaryTypographyProps={{ color: ACCENT_COLOR, fontSize: "0.75rem" }}
        />
      </ListItem>
      <ListItem disablePadding>
        <ListItemText
          primary="If secret key is regenerated, update it in your Google Form add-on."
          primaryTypographyProps={{ color: ACCENT_COLOR, fontSize: "0.75rem" }}
        />
      </ListItem>
      <ListItem disablePadding>
        <ListItemText
          primary="Include main products, upsells, and gifts fields in Google Form for proper ERP mapping."
          primaryTypographyProps={{ color: ACCENT_COLOR, fontSize: "0.75rem" }}
        />
      </ListItem>
    </List>
  </Box>
);

const WebhookManager = () => {
  const muiTheme = useTheme();
  const [webhook, setWebhook] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  const buildWebhookUrl = useCallback(
    (id, key) =>
      id && key
        ? `${window.location.origin}/api/google-form-webhook/${id}/${key}`
        : "",
    []
  );

  useEffect(() => {
    const fetchWebhook = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${baseUrl}/api/webhook/current`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response?.data;

        if (!data) {
          throw new Error("No data returned from server");
        }
        setCompanyId(data.companyId);
        setSecretKey(data.secretKey);
        setWebhook(buildWebhookUrl(data.companyId, data.secretKey));
      } catch (err) {
        console.error(err);
        toast.error("Failed to load current webhook configuration.");
      } finally {
        setLoading(false);
      }
    };
    fetchWebhook();
  }, [buildWebhookUrl]);

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${baseUrl}/api/webhook/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSecretKey(data.secretKey);
      setWebhook(buildWebhookUrl(companyId, data.secretKey));
      toast.success("Secret key regenerated!", { autoClose: 3000 });
    } catch (err) {
      console.error(err);
      toast.error("Failed to regenerate secret key.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (webhook) {
      navigator.clipboard.writeText(webhook);
      toast.info("Webhook URL copied!");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: BACKGROUND_COLOR,
        padding: muiTheme.spacing(4),
      }}
    >
      <ToastContainer />
      <Paper
        elevation={6}
        sx={{
          maxWidth: 650,
          margin: "0 auto",
          padding: muiTheme.spacing(4),
          borderRadius: 2,
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: muiTheme.spacing(3),
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: PRIMARY_TEXT_COLOR }}
          >
            ERP Webhook Manager 🔗
          </Typography>
          <IconButton
            onClick={() => setShowGuide(!showGuide)}
            sx={{ color: ACCENT_COLOR }}
          >
            {showGuide ? <ExpandLessIcon /> : <InfoIcon />}
          </IconButton>
        </Box>

        <Typography
          variant="body1"
          sx={{ mb: muiTheme.spacing(3), color: ACCENT_COLOR }}
        >
          Configure your external sources (Google Forms, CRM) to send
          submissions to ERP securely.
        </Typography>

        <Collapse in={showGuide}>
          <WebhookGuide muiTheme={muiTheme} />
        </Collapse>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress sx={{ color: PRIMARY_TEXT_COLOR }} />
            <Typography sx={{ ml: 2, color: ACCENT_COLOR }}>
              Loading configuration...
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                display: "block",
                mb: 1,
                color: PRIMARY_TEXT_COLOR,
                fontWeight: 500,
              }}
            >
              Webhook Endpoint URL:
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              value={webhook}
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: BACKGROUND_COLOR,
                  color: ACCENT_COLOR,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
              multiline
              rows={2}
              sx={{ mb: 3 }}
              label="Secure Webhook URL"
            />

            <Box
              sx={{
                display: "flex",
                gap: muiTheme.spacing(2),
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                onClick={handleCopy}
                startIcon={<ContentCopyIcon />}
                disabled={!webhook}
                sx={{
                  backgroundColor: ACCENT_COLOR,
                  "&:hover": { backgroundColor: PRIMARY_TEXT_COLOR },
                }}
              >
                Copy URL
              </Button>
              <Button
                variant="outlined"
                onClick={handleRegenerate}
                startIcon={<KeyIcon />}
                disabled={!companyId}
                sx={{
                  color: ACCENT_COLOR,
                  borderColor: ACCENT_COLOR,
                  "&:hover": {
                    borderColor: PRIMARY_TEXT_COLOR,
                    backgroundColor: "rgba(52, 73, 94, 0.04)",
                  },
                }}
              >
                Regenerate Secret
              </Button>
            </Box>

            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 3, color: PRIMARY_TEXT_COLOR }}
            >
              ⚠️ Secret Key:{" "}
              <code style={{ color: ACCENT_COLOR, userSelect: "all" }}>
                {secretKey}
              </code>{" "}
              (Used for URL and integrity verification)
            </Typography>

            <Box
              sx={{
                mt: 4,
                p: 2,
                border: `1px solid ${BACKGROUND_COLOR}`,
                borderRadius: 1,
                backgroundColor: "rgba(209, 216, 224, 0.5)",
                display: "flex",
                alignItems: "center",
                gap: muiTheme.spacing(2),
              }}
            >
              <SupportAgentIcon
                sx={{ color: PRIMARY_TEXT_COLOR, fontSize: "2rem" }}
              />
              <Typography variant="body2" sx={{ color: PRIMARY_TEXT_COLOR }}>
                Need help? Contact Customer Support for assistance with webhook
                setup or Google Form mapping.
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default WebhookManager;
